import { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  NavLink,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  House,
  Activity as ActivityIcon,
  Layers,
  BarChart3,
  FileText,
  Bell,
  Shield,
  ChevronsUpDown,
  Plus,
  Menu,
  Settings,
  Users,
  Megaphone,
  Sparkles,
  ListTodo,
  Check,
} from "lucide-react";
import * as MenuPrimitive from "@radix-ui/react-dropdown-menu";
import type { Team, Project, CrmCatalog } from "@vibe/shared";
import { useData, post, setCsrf, queryClient, refresh } from "./api";
import { WorkspaceContext, type User } from "./context";
import {
  Avatar,
  Loading,
  IconButton,
  Menu as DropdownMenu,
  useToast,
} from "./components/ui";
const Crm = lazy(() =>
  import("./features/Crm").then((m) => ({ default: m.Crm })),
);
const Tasks = lazy(() =>
  import("./features/Tasks").then((m) => ({ default: m.Tasks })),
);
const Projects = lazy(() =>
  import("./features/Projects").then((m) => ({ default: m.Projects })),
);
const Analytics = lazy(() =>
  import("./features/Analytics").then((m) => ({ default: m.Analytics })),
);
const Marketing = lazy(() =>
  import("./features/Marketing").then((m) => ({ default: m.Marketing })),
);
const Dashboard = lazy(() =>
  import("./features/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const Activity = lazy(() =>
  import("./features/Activity").then((m) => ({ default: m.Activity })),
);
const Agents = lazy(() =>
  import("./features/Agents").then((m) => ({ default: m.Agents })),
);
const Admin = lazy(() =>
  import("./features/Admin").then((m) => ({ default: m.Admin })),
);
const Profile = lazy(() =>
  import("./features/Profile").then((m) => ({ default: m.Profile })),
);
const Login = lazy(() =>
  import("./features/Login").then((m) => ({ default: m.Login })),
);
const Privacy = lazy(() =>
  import("./features/Privacy").then((m) => ({ default: m.Privacy })),
);

const TaskPanel = lazy(() =>
  import("./features/TaskPanel").then((m) => ({ default: m.TaskPanel })),
);
import styles from "./App.module.css";
import { usePageScrollLock } from "./hooks/usePageScrollLock";
import { ProjectSymbol } from "./components/ProjectSymbol";

export default function App() {
  const { data: user, isLoading } = useData<User>("/auth/me");
  const [mobileNav, setMobileNav] = useState(false);
  const { data: projects = [] } = useData<Project[]>("/projects", !!user);
  const [projectId, setProjectId] = useState(() => {
    const saved = localStorage.getItem("vibe-project");
    if (saved) return saved;
    try {
      const previous = JSON.parse(
        localStorage.getItem("vibe-projects") || "{}",
      );
      return previous[localStorage.getItem("vibe-team") || ""] || "";
    } catch {
      return "";
    }
  });
  const project =
    projects.find((p) => p.id === projectId) || projects[0] || null;
  // Legacy workspace IDs are retained internally for existing task history.
  const team = project ? ({ id: project.team_id } as Team) : null;
  const { data: crmCatalog } = useData<CrmCatalog>(
    `/projects/${project?.id}/crm-cms/catalog`,
    !!project,
  );
  const { data: notifications = [], isSuccess: notificationsReady } = useData<
    any[]
  >(
    `/teams/${team?.id}/notifications?project=${project?.id || ""}`,
    !!team && !!project,
  );
  const navigate = useNavigate(),
    location = useLocation(),
    toast = useToast();
  usePageScrollLock(mobileNav);
  useEffect(() => {
    if (user) {
      setCsrf(user.csrf);
      document.documentElement.dataset.theme = user.theme;
    }
  }, [user]);
  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname, project?.id]);
  useEffect(() => {
    if (!team) return;
    let stream: EventSource | null = null;
    const connect = () => {
      if (stream) return;
      stream = new EventSource(`/api/v1/teams/${team.id}/events`);
      stream.onerror = () => {
        // A revoked final assignment closes the old workspace stream.
        void queryClient.invalidateQueries({ queryKey: ["/projects"] });
      };
      stream.onmessage = (event) => {
        let noteEvent = false;
        try {
          noteEvent = String(JSON.parse(event.data).kind || "").startsWith(
            "note.",
          );
        } catch {}
        // Frequent autosaves must not refetch every unrelated workspace view.
        queryClient.invalidateQueries({
          predicate: (q) =>
            noteEvent
              ? String(q.queryKey[0]).startsWith(`/teams/${team.id}/notes`)
              : q.queryKey[0] !== "/auth/me",
        });
      };
    };
    const disconnect = () => {
      stream?.close();
      stream = null;
    };
    connect();
    window.addEventListener("pagehide", disconnect);
    window.addEventListener("pageshow", connect);
    return () => {
      disconnect();
      window.removeEventListener("pagehide", disconnect);
      window.removeEventListener("pageshow", connect);
    };
  }, [team?.id]);
  const seenNotifications = useRef<Set<string> | null>(null);
  useEffect(() => {
    seenNotifications.current = null;
  }, [team?.id, project?.id]);
  useEffect(() => {
    if (!team || !notificationsReady) return;
    if (seenNotifications.current)
      for (const n of notifications)
        if (!seenNotifications.current.has(n.id) && !n.read_at) toast(n.title);
    seenNotifications.current = new Set(notifications.map((n) => n.id));
  }, [notifications, team?.id, notificationsReady]);
  function setProject(id: string, preserveRoute = false) {
    setProjectId(id);
    localStorage.setItem("vibe-project", id);
    setMobileNav(false);
    if (!preserveRoute) navigate(location.pathname);
  }
  if (isLoading)
    return (
      <div className="app-loading">
        <img src="/vibe.svg" />
        <Loading />
      </div>
    );
  if (location.pathname === "/privacy")
    return (
      <Suspense fallback={<Loading />}>
        <Privacy />
      </Suspense>
    );
  if (!user)
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<Login />} />
          <Route path="/invite" element={<Login />} />
          <Route path="/reset" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  const unread =
    notifications[0]?.unread_count ??
    notifications.filter((n) => !n.read_at).length;
  const links = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/tasks", label: "Tasks", icon: ListTodo },
    { to: "/marketing", label: "Marketing", icon: Megaphone },
    ...(crmCatalog?.collections.some((c) => c.workspace === "cms")
      ? [{ to: "/cms", label: "CMS", icon: FileText }]
      : []),
    ...(crmCatalog?.collections.some((c) => c.workspace === "crm")
      ? [{ to: "/crm", label: "CRM", icon: Users }]
      : []),
    { to: "/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/activity", label: "Activity", icon: ActivityIcon },
  ];
  return (
    <WorkspaceContext.Provider
      value={{ user, team, project, projects, setProject }}
    >
      <div className={styles.shell}>
        {mobileNav && (
          <div
            className="mobile-nav-overlay"
            onClick={() => setMobileNav(false)}
          />
        )}
        <aside className={`${styles.sidebar} ${mobileNav ? styles.open : ""}`}>
          <div className="sidebar-brand">
            <a className="wordmark" href="/dashboard">
              vibe
              <span />
            </a>
          </div>
          <nav>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to}>
                <l.icon size={17} />
                <span>{l.label}</span>
                {l.to === "/activity" && unread > 0 && (
                  <span className="nav-badge">{unread}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-divider" />
          <nav>
            <NavLink to="/viby">
              <Sparkles size={17} />
              <span>Viby</span>
            </NavLink>
            <NavLink to="/projects">
              <Layers size={17} />
              <span>Projects</span>
            </NavLink>
          </nav>
          <div className="sidebar-divider" />
          <nav>
            <NavLink to="/profile">
              <Settings size={17} />
              <span>Preferences</span>
            </NavLink>
            {user.role === "admin" && (
              <NavLink to="/admin">
                <Shield size={17} />
                <span>Administration</span>
                <span className="admin-dot" />
              </NavLink>
            )}
          </nav>
          <div className="sidebar-bottom">
            <div className="user-menu">
              <Avatar actor={{ ...user, kind: "human" }} size={31} />
              <div>
                <strong>{user.name}</strong>
                <small>
                  {user.role === "admin" ? "Administrator" : "Member"}
                </small>
              </div>
              <DropdownMenu
                items={[
                  {
                    label: "Profile & preferences",
                    onClick: () => navigate("/profile"),
                  },
                  {
                    label: "Sign out",
                    onClick: async () => {
                      await post("/auth/logout");
                      setCsrf("");
                      queryClient.clear();
                      navigate("/login");
                      refresh();
                    },
                  },
                ]}
              />
            </div>
          </div>
        </aside>
        <div
          className={`${styles.main} ${location.pathname === "/viby" ? styles.chatMain : ""}`}
        >
          <header className={styles.topbar}>
            <div>
              <IconButton
                label="Open navigation"
                className="mobile-menu"
                onClick={() => setMobileNav(!mobileNav)}
              >
                <Menu size={19} />
              </IconButton>
              <MenuPrimitive.Root>
                <MenuPrimitive.Trigger
                  className="workspace-switcher topbar-project-switcher"
                  aria-label="Switch project"
                >
                  {project ? (
                    <ProjectSymbol project={project} />
                  ) : (
                    <Layers size={20} />
                  )}
                  <span className="workspace-switcher-labels">
                    <small>Project</small>
                    <strong>{project?.name || "Choose a project"}</strong>
                  </span>
                  <ChevronsUpDown size={14} />
                </MenuPrimitive.Trigger>
                <MenuPrimitive.Portal>
                  <MenuPrimitive.Content
                    className="menu-content workspace-menu-content"
                    align="start"
                    sideOffset={8}
                    collisionPadding={8}
                  >
                    {projects.map((option) => (
                      <MenuPrimitive.Item
                        key={option.id}
                        className="menu-item workspace-project-item"
                        aria-current={
                          option.id === project?.id ? "true" : undefined
                        }
                        onSelect={() => setProject(option.id)}
                      >
                        <ProjectSymbol project={option} />
                        <span className="workspace-project-name">
                          {option.name}
                        </span>
                        <span
                          className="workspace-project-check"
                          aria-hidden="true"
                        >
                          {option.id === project?.id && <Check size={14} />}
                        </span>
                      </MenuPrimitive.Item>
                    ))}
                    {!projects.length && (
                      <MenuPrimitive.Item disabled className="menu-item">
                        No projects assigned
                      </MenuPrimitive.Item>
                    )}
                    {user.role === "admin" && (
                      <>
                        <MenuPrimitive.Separator className="menu-separator" />
                        <MenuPrimitive.Item
                          className="menu-item"
                          aria-label="Create a new project"
                          onSelect={() => navigate("/projects?create=1")}
                        >
                          <Plus size={14} />
                          New project
                        </MenuPrimitive.Item>
                      </>
                    )}
                  </MenuPrimitive.Content>
                </MenuPrimitive.Portal>
              </MenuPrimitive.Root>
            </div>
            <div>
              <IconButton
                label="View activity"
                onClick={() => navigate("/activity")}
              >
                <Bell size={17} />
                {unread > 0 && <i className="notification-dot" />}
              </IconButton>
              <Avatar actor={{ ...user, kind: "human" }} size={25} />
            </div>
          </header>
          <main
            className={`${styles.content} ${location.pathname === "/viby" ? styles.chatContent : ""}`}
          >
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/board"
                  element={
                    <Navigate
                      to={{ pathname: "/tasks", search: location.search }}
                      replace
                    />
                  }
                />
                <Route path="/tasks" element={<Tasks key={project?.id} />} />
                <Route path="/projects" element={<Projects />} />
                <Route
                  path="/files"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/notes"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/documentation"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route path="/marketing" element={<Marketing />} />
                <Route path="/viby" element={<Agents />} />
                <Route
                  path="/agents"
                  element={<Navigate to="/viby" replace />}
                />
                <Route path="/analytics" element={<Analytics />} />
                <Route
                  path="/reports"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/publish"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route path="/activity" element={<Activity />} />
                <Route
                  path="/notifications"
                  element={<Navigate to="/activity" replace />}
                />
                <Route path="/cms" element={<Crm workspace="cms" />} />
                <Route path="/crm" element={<Crm workspace="crm" />} />
                <Route
                  path="/crm-cms"
                  element={
                    <Navigate
                      to={
                        crmCatalog?.collections.some(
                          (c) => c.workspace === "cms",
                        )
                          ? "/cms"
                          : "/crm"
                      }
                      replace
                    />
                  }
                />
                <Route path="/admin" element={<Admin />} />
                <Route path="/profile" element={<Profile />} />
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </Suspense>
          </main>
        </div>
        {new URLSearchParams(location.search).has("task") && (
          <Suspense fallback={null}>
            <TaskPanel />
          </Suspense>
        )}
      </div>
    </WorkspaceContext.Provider>
  );
}
